import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import {
  classifyDimension,
  type DimensionCriterion,
  type DimensionMode,
} from '@viewer/lib/itemDimensions'

// ============================================================================
// filter: multi-criterion query engine
//
// A `FilterQuery` is a list of `FilterRule`s combined with AND or OR.
// Each rule classifies items by some dimension (entity, storey, etc.)
// and accepts/rejects each item based on operator + value.
//
// `evaluateFilterQuery` runs the query across every loaded model and
// applies the resulting set as the viewer's selection.
// ============================================================================

export type FilterOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'startsWith'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'

export const STRING_OPERATORS: FilterOperator[] = [
  'equals',
  'notEquals',
  'contains',
  'startsWith',
]

export const NUMERIC_OPERATORS: FilterOperator[] = [
  'equals',
  'notEquals',
  'gt',
  'gte',
  'lt',
  'lte',
]

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  equals: '=',
  notEquals: '≠',
  contains: 'contém',
  startsWith: 'começa por',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
}

export interface FilterRule {
  /** Stable identifier for React keys + remove operations. */
  id: string
  dimension: DimensionMode
  /** Only meaningful when dimension === 'property'. */
  propertyKey?: string
  operator: FilterOperator
  /** One or more candidate values. String operators OR across them
   *  (item passes if its bucket matches ANY of the values; for
   *  notEquals it must match NONE). Numeric operators only honour
   *  values[0]. */
  values: string[]
}

export function isNumericOperator(op: FilterOperator): boolean {
  return op === 'gt' || op === 'gte' || op === 'lt' || op === 'lte'
}

export interface FilterQuery {
  combinator: 'and' | 'or'
  rules: FilterRule[]
}

function ruleCriterion(rule: FilterRule): DimensionCriterion {
  return rule.dimension === 'property'
    ? { mode: 'property', propertyKey: rule.propertyKey ?? '' }
    : { mode: rule.dimension }
}

function evalOperator(
  bucketValue: string,
  operator: FilterOperator,
  values: string[],
): boolean {
  if (values.length === 0) return false
  switch (operator) {
    case 'equals':
      // OR over candidate values: bucket matches if it equals any.
      return values.some((v) => bucketValue === v)
    case 'notEquals':
      // OR-flipped: bucket fails if it equals any candidate.
      return values.every((v) => bucketValue !== v)
    case 'contains':
      // OR: bucket passes if it contains any candidate (case-insensitive).
      return values.some((v) =>
        bucketValue.toLowerCase().includes(v.toLowerCase()),
      )
    case 'startsWith':
      // OR: bucket passes if it starts with any candidate (case-insensitive).
      return values.some((v) =>
        bucketValue.toLowerCase().startsWith(v.toLowerCase()),
      )
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      // Numeric operators only honour the first value.
      const a = Number(bucketValue)
      const b = Number(values[0])
      if (Number.isNaN(a) || Number.isNaN(b)) return false
      if (operator === 'gt') return a > b
      if (operator === 'gte') return a >= b
      if (operator === 'lt') return a < b
      return a <= b
    }
  }
}

interface ModelMatchKey {
  modelId: string
  localId: number
}

function keyOf(t: ModelMatchKey): string {
  return `${t.modelId}:${t.localId}`
}

/** Run one rule against every loaded model and return the set of
 *  matching `modelId:localId` keys. */
async function evaluateRule(rule: FilterRule): Promise<Set<string>> {
  const matches = new Set<string>()
  const criterion = ruleCriterion(rule)
  // Strip empties; trim every value once.
  const values = rule.values.map((v) => v.trim()).filter((v) => v !== '')
  if (values.length === 0) return matches
  for (const [modelId, model] of viewerApi.iterModels()) {
    const map = await classifyDimension(model, criterion)
    for (const [localId, bucketValue] of map) {
      if (evalOperator(bucketValue, rule.operator, values)) {
        matches.add(keyOf({ modelId, localId }))
      }
    }
    // For "notEquals" we also want items that have NO bucket assignment
    // for this dimension: they trivially "don't equal" any value. We
    // skip those for now to keep semantics predictable: notEquals only
    // matches items the dimension can classify.
  }
  return matches
}

/** Evaluate the full query (all rules combined by combinator) and
 *  return the matching selection targets. */
export async function evaluateFilterQuery(
  query: FilterQuery,
): Promise<{ modelId: string; localId: number }[]> {
  const validRules = query.rules.filter((r) => {
    if (r.dimension === 'property' && !(r.propertyKey ?? '').trim()) {
      return false
    }
    if (r.values.every((v) => v.trim() === '')) return false
    return true
  })
  if (validRules.length === 0) return []

  let combined: Set<string> | null = null
  for (const rule of validRules) {
    const matches = await evaluateRule(rule)
    if (combined === null) {
      combined = matches
    } else if (query.combinator === 'and') {
      const next = new Set<string>()
      for (const k of combined) if (matches.has(k)) next.add(k)
      combined = next
    } else {
      for (const k of matches) combined.add(k)
    }
    if (query.combinator === 'and' && combined.size === 0) break
  }
  if (!combined) return []

  const out: { modelId: string; localId: number }[] = []
  for (const k of combined) {
    const idx = k.lastIndexOf(':')
    if (idx < 0) continue
    out.push({
      modelId: k.slice(0, idx),
      localId: Number(k.slice(idx + 1)),
    })
  }
  return out
}

/** Convenience: evaluate the query and push the result as the viewer's
 *  selection (replacing whatever was selected before). Returns the
 *  match count for the caller to display. */
export async function applyFilterAsSelection(
  query: FilterQuery,
): Promise<number> {
  const targets = await evaluateFilterQuery(query)
  useAppStore.getState().applyBulkSelection(targets, 'replace')
  return targets.length
}
